import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import { nameof } from "../../../../../utils/nameof";
import { AssetInventoryCompletenessSection } from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { describeMissingPropertyRightField } from "../OtherPropertyRightsView.missingFields";
import { PropertyRightsFormValues } from "../OtherPropertyRightsView.types";
import {
  PROPERTY_RIGHT_KINDS,
  PropertyRightKindConfig,
  fromPropertyRightsResponse,
  hasRightsOf,
  toPropertyRightsRequest,
} from "../OtherPropertyRightsView.utils";
import { createPropertyRightsSchema } from "../OtherPropertyRightsView.validation";

export function useAssetPropertyRights(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);

  const queryKey = [ClientFileQueryKey.AssetInventoryPropertyRights, clientId];

  const { data } = useQuery({
    queryKey,
    queryFn: () => clientsApi.getAssetInventoryPropertyRights({ clientId }),
    enabled: !!clientId,
  });

  const uploadDocuments = async (
    { area, queryKey: listQueryKey }: PropertyRightKindConfig,
    files: File[]
  ): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryAssetDocument(
            { clientId, area },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [listQueryKey, clientId] });
    return failed;
  };

  const validationSchema = useMemo(() => createPropertyRightsSchema(t), [t]);

  const initialValues = useMemo(() => fromPropertyRightsResponse(data), [data]);

  const formik = useFormikWithDirtyForm<PropertyRightsFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const request = toPropertyRightsRequest(values);

      if (JSON.stringify(request) !== JSON.stringify(toPropertyRightsRequest(initialValues))) {
        await clientsApi.saveAssetInventoryPropertyRights({ clientId, savePropertyRightsRequest: request });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
        await queryClient.invalidateQueries({ queryKey });
      }

      let uploadFailed = false;
      const hiddenZones = new Set<string>();
      for (const config of PROPERTY_RIGHT_KINDS) {
        const pending = values[config.fieldName];
        if (!hasRightsOf(values.answers, config.kind)) {
          hiddenZones.add(config.zone);
          if (pending.length > 0) {
            await helpers.setFieldValue(config.fieldName, []);
          }
          continue;
        }
        if (pending.length > 0) {
          const failed = await uploadDocuments(config, pending);
          await helpers.setFieldValue(config.fieldName, failed);
          uploadFailed ||= failed.length > 0;
        }
      }

      const moves = values.documentMoves.filter((move) => !hiddenZones.has(move.target));
      const failedMoves = await moveDocuments(moves);
      await helpers.setFieldValue(nameof<PropertyRightsFormValues>("documentMoves"), failedMoves);

      if (!uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.propertyRightsTab.saveSuccess"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.PropertyRights,
    formik.values,
    describeMissingPropertyRightField
  );

  return { formik, homeAddress: data?.homeAddress, missingFields, onAddMove, onCancelMove };
}
